import { isValidEmail } from "./validators.lib";
import { GUEST_EMAIL_DOMAIN } from "../../shared/auth/guest-placeholder";

export const SYNTHETIC_EMAIL_SUFFIXES = [
  "@imported.repackify.com",
  "@importedsalesforceemail.com",
  `@${GUEST_EMAIL_DOMAIN}`,
] as const;

export function isSyntheticEmail(email: string | undefined | null): boolean {
  return (
    typeof email === "string" &&
    SYNTHETIC_EMAIL_SUFFIXES.some((suffix) =>
      email.trim().toLowerCase().endsWith(suffix),
    )
  );
}

export function normalizeRealEmail(
  email: string | undefined | null,
): string | undefined {
  if (typeof email !== "string") return undefined;
  const normalized = email.trim().toLowerCase();
  return normalized !== "" &&
    isValidEmail(normalized) &&
    !isSyntheticEmail(normalized)
    ? normalized
    : undefined;
}

export function isRealEmail(email: string | undefined | null): email is string {
  return normalizeRealEmail(email) !== undefined;
}

type ContactIdentity = {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  jobTitle?: string;
  companyId?: unknown;
  userId?: unknown;
  createdBy?: unknown;
  ownerCompanyId?: unknown;
  prospectId?: unknown;
  contactCompanyId?: unknown;
  source?: string;
  externalId?: string;
  externalSourceName?: string;
  assignedTo?: unknown;
  role?: unknown;
  roles?: unknown;
  notes?: string;
  tags?: string[];
  primaryShippingAddressId?: unknown;
  productInterest?: unknown;
};

function hasValue(value: unknown): boolean {
  return typeof value === "string" ? value.trim() !== "" : value !== undefined;
}

export function isLegacySyntheticContact(contact: ContactIdentity): boolean {
  return (
    isSyntheticEmail(contact.email) &&
    hasValue(contact.companyId) &&
    !hasValue(contact.firstName) &&
    !hasValue(contact.lastName) &&
    !hasValue(contact.phone) &&
    !hasValue(contact.jobTitle) &&
    !hasValue(contact.userId) &&
    !hasValue(contact.createdBy) &&
    !hasValue(contact.ownerCompanyId) &&
    !hasValue(contact.prospectId) &&
    !hasValue(contact.contactCompanyId) &&
    (contact.source === undefined || contact.source === "backfill") &&
    !hasValue(contact.externalId) &&
    !hasValue(contact.externalSourceName) &&
    !hasValue(contact.assignedTo) &&
    !hasValue(contact.role) &&
    !hasValue(contact.roles) &&
    !hasValue(contact.notes) &&
    !hasValue(contact.tags) &&
    !hasValue(contact.primaryShippingAddressId) &&
    !hasValue(contact.productInterest)
  );
}

export function hasGenuineContactIdentity(contact: ContactIdentity): boolean {
  return (
    !isSyntheticEmail(contact.email) &&
    [
      contact.email,
      contact.firstName,
      contact.lastName,
      contact.phone,
      contact.userId,
      contact.externalId,
    ].some(hasValue)
  );
}
